import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Settings as SettingsIcon, Palette, Building2, Save, MessageCircle, Share2 } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';
import { useToast } from '../../hooks/use-toast';

const Settings = () => {
  const { branding, updateBranding } = useBranding();
  const { toast } = useToast();
  const [formData, setFormData] = useState(branding);
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updateBranding(formData);
    setSaving(false);
    
    if (result.success) {
      toast({
        title: 'Settings Saved',
        description: 'Your practice settings have been updated successfully.'
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Practice Settings</h1>
          <p className="text-gray-600 mt-2">Configure your practice branding and information</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="branding">
            <Palette className="mr-2 h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="practice">
            <Building2 className="mr-2 h-4 w-4" />
            Practice Info
          </TabsTrigger>
          <TabsTrigger value="integrations">
            <MessageCircle className="mr-2 h-4 w-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="advanced">
            <SettingsIcon className="mr-2 h-4 w-4" />
            Advanced
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Brand Identity</CardTitle>
              <CardDescription>
                Customize how your practice appears to patients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="practiceName">Practice Name</Label>
                <Input
                  id="practiceName"
                  value={formData.practiceName}
                  onChange={(e) => handleChange('practiceName', e.target.value)}
                  placeholder="Acme Med Spa"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="logoUrl">Logo URL</Label>
                <Input
                  id="logoUrl"
                  value={formData.logoUrl || ''}
                  onChange={(e) => handleChange('logoUrl', e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Upload your logo to a hosting service and paste the URL here
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="primaryColor"
                      type="color"
                      value={formData.primaryColor?.includes('hsl') ? '#DCC3B0' : formData.primaryColor}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={formData.primaryColor}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                      placeholder="#DCC3B0"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondaryColor">Secondary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="secondaryColor"
                      type="color"
                      value={formData.secondaryColor?.includes('hsl') ? '#F5F5F5' : formData.secondaryColor}
                      onChange={(e) => handleChange('secondaryColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={formData.secondaryColor}
                      onChange={(e) => handleChange('secondaryColor', e.target.value)}
                      placeholder="#F5F5F5"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="accentColor">Accent Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="accentColor"
                      type="color"
                      value={formData.accentColor?.includes('hsl') ? '#E8D5C4' : formData.accentColor}
                      onChange={(e) => handleChange('accentColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={formData.accentColor}
                      onChange={(e) => handleChange('accentColor', e.target.value)}
                      placeholder="#E8D5C4"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Practice Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Premium aesthetic and wellness treatments"
                  rows={3}
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Practice Info Tab */}
        <TabsContent value="practice" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                How patients can reach your practice
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contactEmail">Email Address</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleChange('contactEmail', e.target.value)}
                    placeholder="hello@acmemedspa.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="contactPhone">Phone Number</Label>
                  <Input
                    id="contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => handleChange('contactPhone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="123 Wellness Ave, Suite 100"
                  className="mt-2"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Los Angeles"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="CA"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="zipCode">Zip Code</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    placeholder="90001"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://acmemedspa.com"
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          {/* AI Chatbot Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                AI Chatbot
              </CardTitle>
              <CardDescription>
                Customize your AI assistant's name and personality
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="chatbotName">Chatbot Name</Label>
                  <Input
                    id="chatbotName"
                    value={formData.chatbotName || ''}
                    onChange={(e) => handleChange('chatbotName', e.target.value)}
                    placeholder="e.g., Elle, Aria, Sophie"
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This name appears in the chat header and welcome message
                  </p>
                </div>
              </div>
              <div>
                <Label htmlFor="chatbotWelcomeMessage">Welcome Message</Label>
                <Textarea
                  id="chatbotWelcomeMessage"
                  value={formData.chatbotWelcomeMessage || ''}
                  onChange={(e) => handleChange('chatbotWelcomeMessage', e.target.value)}
                  placeholder="Hi! I'm your AI Beauty Advisor. How can I help you today?"
                  rows={3}
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  The first message patients see when they open the chatbot
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Booking System Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Booking System</CardTitle>
              <CardDescription>
                Connect your online booking system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="boulevardBusinessId">Boulevard Business ID</Label>
                <Input
                  id="boulevardBusinessId"
                  value={formData.boulevardBusinessId || ''}
                  onChange={(e) => handleChange('boulevardBusinessId', e.target.value)}
                  placeholder="e.g., 5e7e7c8d-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="mt-2 font-mono text-sm"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Find this in your Boulevard dashboard under Settings → Developer
                </p>
              </div>
              <div>
                <Label htmlFor="businessHours">Business Hours</Label>
                <Input
                  id="businessHours"
                  value={formData.businessHours || ''}
                  onChange={(e) => handleChange('businessHours', e.target.value)}
                  placeholder="e.g., Mon-Sat: 9am-7pm"
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Displayed on your Help page contact cards
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Once you enter your Boulevard Business ID, the "Book Now" button will open Boulevard's booking widget for your spa.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Social Media Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Social Media Links
              </CardTitle>
              <CardDescription>
                Connect your social profiles (shown in footer)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instagramUrl">Instagram</Label>
                  <Input
                    id="instagramUrl"
                    type="url"
                    value={formData.instagramUrl || ''}
                    onChange={(e) => handleChange('instagramUrl', e.target.value)}
                    placeholder="https://instagram.com/yourspa"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="facebookUrl">Facebook</Label>
                  <Input
                    id="facebookUrl"
                    type="url"
                    value={formData.facebookUrl || ''}
                    onChange={(e) => handleChange('facebookUrl', e.target.value)}
                    placeholder="https://facebook.com/yourspa"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="tiktokUrl">TikTok</Label>
                  <Input
                    id="tiktokUrl"
                    type="url"
                    value={formData.tiktokUrl || ''}
                    onChange={(e) => handleChange('tiktokUrl', e.target.value)}
                    placeholder="https://tiktok.com/@yourspa"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="youtubeUrl">YouTube</Label>
                  <Input
                    id="youtubeUrl"
                    type="url"
                    value={formData.youtubeUrl || ''}
                    onChange={(e) => handleChange('youtubeUrl', e.target.value)}
                    placeholder="https://youtube.com/@yourspa"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="twitterUrl">Twitter/X</Label>
                  <Input
                    id="twitterUrl"
                    type="url"
                    value={formData.twitterUrl || ''}
                    onChange={(e) => handleChange('twitterUrl', e.target.value)}
                    placeholder="https://twitter.com/yourspa"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedinUrl">LinkedIn</Label>
                  <Input
                    id="linkedinUrl"
                    type="url"
                    value={formData.linkedinUrl || ''}
                    onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                    placeholder="https://linkedin.com/company/yourspa"
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Advanced Settings</CardTitle>
              <CardDescription>
                Domain and technical configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="subdomain">Subdomain</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="subdomain"
                    value={formData.subdomain}
                    onChange={(e) => handleChange('subdomain', e.target.value)}
                    placeholder="acmemedspa"
                    className="flex-1"
                  />
                  <div className="flex items-center text-gray-500">.mdspa360.com</div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Your patients will visit: {formData.subdomain}.mdspa360.com
                </p>
              </div>

              <div className="bg-secondary rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Custom Domain</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Want to use your own domain like book.acmemedspa.com? Contact support to set up custom domain mapping.
                </p>
                <Button variant="outline" size="sm">
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;